import React from 'react';
import { IntegrationCard as IntegrationCardType, ShelterLuvStatus } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import styles from './Integrations.module.scss';

const ICONS: Record<string, React.ReactNode> = {
  shelterluv: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  petfinder: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  'adopt-a-pet': (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

interface IntegrationCardProps {
  integration: IntegrationCardType;
  shelterLuvStatus?: ShelterLuvStatus;
  onConnect: () => void;
  onDisconnect: () => void;
}

export default function IntegrationCard({
  integration,
  shelterLuvStatus,
  onConnect,
  onDisconnect,
}: IntegrationCardProps) {
  const isConnected = integration.id === 'shelterluv' && shelterLuvStatus?.connected;
  const isPlanned = integration.status === 'planned';

  const accentClass = isConnected
    ? styles.accentGreen
    : integration.id === 'shelterluv'
      ? styles.accentYellow
      : styles.accentSubtle;

  return (
    <div className={styles.card}>
      <div className={`${styles.cardAccent} ${accentClass}`} />

      <div className={styles.cardHeader}>
        <div className={`${styles.cardIcon} ${integration.id === 'shelterluv' ? styles.cardIconActive : ''}`}>
          {ICONS[integration.id]}
        </div>
        <div>
          <h3 className={styles.cardTitle}>{integration.name}</h3>
          {isConnected ? (
            <StatusBadge variant="success">Connected</StatusBadge>
          ) : isPlanned ? (
            <StatusBadge variant="neutral">Planned</StatusBadge>
          ) : (
            <StatusBadge variant="neutral">Not Connected</StatusBadge>
          )}
        </div>
      </div>

      {isConnected && shelterLuvStatus ? (
        <>
          <div className={styles.dataGrid}>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Organization</span>
              <span className={styles.dataValue}>{shelterLuvStatus.orgName}</span>
            </div>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Chips Imported</span>
              <span className={styles.dataValue}>{shelterLuvStatus.chipsImported}</span>
            </div>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Last Import</span>
              <span className={styles.dataValue}>{formatDate(shelterLuvStatus.lastSync)}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" className={styles.disconnectBtn} onClick={onDisconnect}>
            Disconnect
          </Button>
        </>
      ) : (
        <>
          <p className={styles.cardDescription}>{integration.description}</p>

          {integration.features && (
            <ul className={styles.featureList}>
              {integration.features.map((feature) => (
                <li key={feature} className={styles.featureItem}>
                  {feature}
                </li>
              ))}
            </ul>
          )}

          {isPlanned ? (
            <span className={styles.comingSoon}>Coming Soon</span>
          ) : (
            <Button variant="green" size="sm" onClick={onConnect}>
              Connect
            </Button>
          )}
        </>
      )}
    </div>
  );
}
